import 'react-quill/dist/quill.snow.css';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { FileUpload } from 'primereact/fileupload';
import { Image } from 'primereact/image';
import { connect } from 'react-redux';
import { arrayIncludes } from '@material-ui/pickers/_helpers/utils';
import ReactQuill from 'react-quill';
import {
    updateSimcard, addSimcard, getSimcard,
} from '../../../stores/actions/installerActions';
import { getSystemsNodesShort, getSystemsNodesRoutersShort } from '../../../stores/actions/logsActions';

interface SimcardEditDialogProps {
    visible: boolean;
    initialSimcard?: any;
    onHide: () => void;
    // from Redux
    systemsNodesShort: any[];
    systemsNodesRoutersShort: any[];
    mobileView: boolean;
    dbSimcards: any[];
    currentSimcard: any;
    updateSimcardSuccess: boolean;
    createSimcardSuccess: boolean;
    createSimcardFail: boolean;
    updateSimcard: (simcard) => void;
    addSimcard: (simcard) => void;
    getSimcard: (simcardId) => void;
    getSystemsNodesShort: () => void;
    getSystemsNodesRoutersShort: () => void;
}

const networks = ['Orange', 'T-Mobile', 'Plus', 'other'];
const owners = ['customer', 'WISENE'];
const purposes = ['data', 'SMS'];

const emptySimcard = {
    ID: null,
    systemId: null, systemName: null,
    ip: null, telephone: null,
    purpose: 'data', iccid: null,
    network: 'other', pin: null, puk: null,
    owner: 'WISENE', notes: null,
    pinDisabled: false,
    imageData: null, imageMime: null,
    nodeId: null, nodeName: null,
    routerId: null, routerSerialNumber: null,
    previousNodeId: null, previousRouterId: null,
    previousRouterSerialNumber: null,
    previousNodeName: null, previousSystemName: null
};

const validators = {
    iccid: (val: string) => /^89\d{16,20}$/.test(val),
    ip: (val: string) => /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(val),
    telephone: (val: string) => /^\+\d{11,13}$/.test(val),
    pin: (val: string) => /^(disabled|\d{1,10})$/.test(val),
    puk: (val: string) => /^\d{1,10}$/.test(val),
    ipRequiredForData: (purpose: string | null, ip: string | null) => purpose !== 'data' || !!ip,
    telephoneRequiredForSms: (purpose: string | null, tel: string | null) => purpose !== 'SMS' || !!tel,
};

const SimcardEditDialog = ({
    visible, initialSimcard, onHide,
    systemsNodesShort, systemsNodesRoutersShort, mobileView,
    dbSimcards, currentSimcard,
    updateSimcardSuccess,
    updateSimcard, addSimcard, getSimcard,
    getSystemsNodesShort, getSystemsNodesRoutersShort
}: SimcardEditDialogProps) => {

    const [simcard, setSimcard] = useState(emptySimcard);
    const fileUploadRef = useRef<any>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);

    useEffect(() => {
        if (visible) {
            setSimcard(initialSimcard ? { ...emptySimcard, ...initialSimcard } : emptySimcard);
            setUploadError(null);
        }
    }, [visible, initialSimcard]);

    useEffect(() => {
        if (visible) {
            getSystemsNodesShort();
            getSystemsNodesRoutersShort();
        }
    }, [visible]);

    useEffect(() => {
        if (visible && simcard.ID) getSimcard(simcard.ID);
    }, [visible, simcard.ID]);

    useEffect(() => {
        if (currentSimcard) {
            setSimcard(prev => ({
                ...prev,
                imageData: currentSimcard.imageData,
                imageMime: currentSimcard.imageMime,
                previousNodeId: prev.nodeId,
                previousRouterId: prev.routerId,
                previousRouterSerialNumber: prev.routerSerialNumber,
                previousNodeName: prev.nodeName,
                previousSystemName: prev.systemName,
            }));
        }
    }, [currentSimcard]);

    // Close on success
    useEffect(() => {
        if (updateSimcardSuccess) {
            setTimeout(() => handleHide(), 50); // let Redux finish propagating first
        }
    }, [updateSimcardSuccess]);

    const handleHide = () => {
        setSimcard(emptySimcard);
        onHide();
    };

    const isUnique = (field: 'iccid' | 'telephone' | 'ip', value: string, currentId?: number) => {
        if (!value) return true;
        return !dbSimcards?.some(sc => sc[field] === value && sc.ID !== currentId);
    };

    const saveSimcard = () => {
        if (!isUnique('iccid', simcard.iccid ?? '', simcard.ID ?? 0)
            || !isUnique('telephone', simcard.telephone ?? '', simcard.ID ?? 0)
            || !isUnique('ip', simcard.ip ?? '', simcard.ID ?? 0)) return;
        if (simcard.iccid && !validators.iccid(simcard.iccid)) return;
        if (simcard.ip && !validators.ip(simcard.ip)) return;
        if (simcard.telephone && !validators.telephone(simcard.telephone)) return;
        if (simcard.pin && !validators.pin(simcard.pin)) return;
        if (simcard.puk && !validators.puk(simcard.puk)) return;
        if (!validators.ipRequiredForData(simcard.purpose, simcard.ip)) return;
        if (!validators.telephoneRequiredForSms(simcard.purpose, simcard.telephone)) return;

        simcard.ID ? updateSimcard(simcard) : addSimcard(simcard);
    };

    const onInputChange = (e, name) => {
        let val = e.value !== undefined ? e.value : e.target?.value ?? null;

        if (val === '') val = null;

        if (name === 'nodeId' && val) {
            const sn = systemsNodesShort.find(item => item.nodeId === val);
            setSimcard(prev => ({
                ...prev,
                nodeId: val, nodeName: sn.nodeName,
                systemId: sn.systemId, systemName: sn.systemName,
                routerId: null, routerSerialNumber: null
            }));
        } else if (name === 'routerId' && val) {
            const snrs = systemsNodesRoutersShort.find(item => item.routerId === val);
            setSimcard(prev => ({
                ...prev,
                routerId: val, routerSerialNumber: snrs.routerSerialNumber,
                nodeId: snrs.nodeId, nodeName: snrs.nodeName,
                systemId: snrs.systemId, systemName: snrs.systemName
            }));
        } else if (name === 'nodeId' && !val) {
            setSimcard(prev => ({ ...prev, nodeId: null, nodeName: null, systemId: null, systemName: null }));
        } else if (name === 'routerId' && !val) {
            setSimcard(prev => ({
                ...prev,
                routerId: null, routerSerialNumber: null,
                nodeId: null, nodeName: null, systemId: null, systemName: null
            }));
        } else {
            setSimcard(prev => ({ ...prev, [name]: val }));
        }
    };

    const handleFileSelect = (e) => {
        const file = e.files[0];
        if (!file) return;
        setUploadError(null);
        const reader = new FileReader();
        reader.onloadend = () => {
            setSimcard(prev => ({
                ...prev,
                imageData: reader.result.split(",")[1],
                imageMime: file.type
            }));
            fileUploadRef.current?.clear();
        };
        reader.onerror = () => {
            setUploadError(`Could not read "${file.name}". Please try again.`);
            fileUploadRef.current?.clear();
        };
        reader.readAsDataURL(file);
    };

    // Derived lists
    const nodeNames = useMemo(() => {
        if (!systemsNodesShort || !dbSimcards) return [];
        const takenNodeIds = dbSimcards.map(el => el.nodeId);
        const freeNodes = systemsNodesShort
            .filter(v => !arrayIncludes(takenNodeIds, v.nodeId) && v.deviceRole === 'gateway')
            .sort((a, b) => a.systemName.localeCompare(b.systemName));
        let result = freeNodes.map(item => ({
            nodeId: item.nodeId,
            nodeName: `${item.systemName} ${item.nodeName}`
        }));
        if (simcard.ID && simcard.nodeId) {
            result = [...result, { nodeId: simcard.nodeId, nodeName: `${simcard.systemName} ${simcard.nodeName}` }];
        } else if (simcard.ID && simcard.previousNodeId) {
            result = [...result, { nodeId: simcard.previousNodeId, nodeName: `${simcard.previousSystemName} ${simcard.previousNodeName}` }];
        }
        return result;
    }, [systemsNodesShort, dbSimcards, simcard.ID, simcard.nodeId]);

    const routerSerialNumbers = useMemo(() => {
        if (!systemsNodesRoutersShort?.length || !dbSimcards) return [];
        const takenRouterIds = dbSimcards.map(el => el.routerId);
        const freeRouters = systemsNodesRoutersShort
            .filter(v => !arrayIncludes(takenRouterIds, v.routerId))
            .sort((a, b) => a.systemName ? a.systemName.localeCompare(b.systemName) : a.routerSerialNumber.localeCompare(b.routerSerialNumber));
        let result = freeRouters.map(item => ({
            routerId: item.routerId,
            routerSerialNumber: `${item.systemName ?? ''} ${item.routerSerialNumber}`
        }));
        if (simcard.ID && simcard.routerId) {
            result = [...result, { routerId: simcard.routerId, routerSerialNumber: `${simcard.systemName ?? ''} ${simcard.routerSerialNumber}` }];
        } else if (simcard.ID && simcard.previousRouterId) {
            result = [...result, { routerId: simcard.previousRouterId, routerSerialNumber: simcard.previousRouterSerialNumber }];
        }
        return result;
    }, [systemsNodesRoutersShort, dbSimcards, simcard.ID, simcard.routerId, simcard.previousRouterId]);

    const dialogFooter = (
        <React.Fragment>
            <Button label="Cancel" icon="pi pi-times" outlined onClick={handleHide} />
            <Button label="Save" icon="pi pi-check" onClick={saveSimcard} />
        </React.Fragment>
    );

    return (
        <Dialog
            visible={visible}
            style={{ width: mobileView ? '100%' : '42rem' }}
            breakpoints={{ '960px': '75vw', '641px': '90vw' }}
            modal className="p-fluid" footer={dialogFooter} onHide={handleHide}
            header={simcard.ID ? `Edit SIM card #${simcard.ID}` : 'Add new SIM card'}
        >
            <div className="formgrid grid">
                <div className="field">
                    <label className="mb-3 font-bold">Node</label>
                    <Dropdown
                        value={simcard.routerId ? null : simcard.nodeId} onChange={(e) => onInputChange(e, 'nodeId')}
                        options={nodeNames} optionLabel="nodeName" optionValue="nodeId"
                        placeholder={simcard.routerId ? 'Inherited from router' : 'Select gateway'} className="md:w-28rem"
                        filter disabled={!!simcard.routerId} showClear filterPlaceholder="Type to search..."
                    />
                </div>
                <div className="field">
                    <label className="mb-3 font-bold">Router</label>
                    <Dropdown
                        value={simcard.routerId} onChange={(e) => onInputChange(e, 'routerId')}
                        options={routerSerialNumbers} optionLabel="routerSerialNumber" optionValue="routerId"
                        placeholder="Select router" className="md:w-28rem"
                        filter disabled={!!simcard.nodeId && !simcard.routerId} showClear filterPlaceholder="Type to search..."
                    />
                </div>
            </div>

            <div className="formgrid grid">
                <div className="field col">
                    <label className="mb-3 font-bold">IP</label>
                    <InputText value={simcard.ip} maxLength={15} onChange={(e) => onInputChange(e, 'ip')}
                        invalid={!isUnique('ip', simcard.ip ?? '', simcard.ID ?? 0) || !validators.ipRequiredForData(simcard.purpose, simcard.ip)} />
                    {!isUnique('ip', simcard.ip ?? '', simcard.ID ?? 0) && <small className="p-error">Already in use</small>}
                    {simcard.ip && !validators.ip(simcard.ip) && <small className="p-error">Invalid IP address</small>}
                    {!validators.ipRequiredForData(simcard.purpose, simcard.ip) && <small className="p-error">IP is required when purpose is data</small>}
                </div>
                <div className="field col">
                    <label className="mb-3 font-bold">Telephone</label>
                    <InputText value={simcard.telephone} maxLength={14} onChange={(e) => onInputChange(e, 'telephone')}
                        invalid={!isUnique('telephone', simcard.telephone ?? '', simcard.ID ?? 0) || !validators.telephoneRequiredForSms(simcard.purpose, simcard.telephone)} />
                    {!isUnique('telephone', simcard.telephone ?? '', simcard.ID ?? 0) && <small className="p-error">Already in use</small>}
                    {simcard.telephone && !validators.telephone(simcard.telephone) && <small className="p-error">Invalid phone number</small>}
                    {!validators.telephoneRequiredForSms(simcard.purpose, simcard.telephone) && <small className="p-error">Telephone is required when purpose is SMS</small>}
                </div>
                <div className="field col">
                    <label className="mb-3 font-bold">ICCID</label>
                    <InputText value={simcard.iccid} maxLength={22} onChange={(e) => onInputChange(e, 'iccid')}
                        invalid={!isUnique('iccid', simcard.iccid ?? '', simcard.ID ?? 0)} />
                    {!isUnique('iccid', simcard.iccid ?? '', simcard.ID ?? 0) && <small className="p-error">Already in use</small>}
                    {simcard.iccid && !validators.iccid(simcard.iccid) && <small className="p-error">Invalid ICCID</small>}
                </div>
            </div>

            <div className="formgrid grid">
                <div className="field col">
                    <label className="mb-3 font-bold">Purpose</label>
                    <Dropdown value={simcard.purpose} onChange={(e) => onInputChange(e, 'purpose')}
                        options={purposes} optionLabel="purpose" filter
                        placeholder={simcard.purpose ?? "Select purpose"} invalid={!simcard.purpose} />
                </div>
                <div className="field col">
                    <label className="mb-3 font-bold">Owner</label>
                    <Dropdown value={simcard.owner} onChange={(e) => onInputChange(e, 'owner')}
                        options={owners} optionLabel="owner" filter
                        placeholder={simcard.owner ?? "Select owner"} invalid={!simcard.owner} />
                </div>
                <div className="field col">
                    <label className="mb-3 font-bold">Network</label>
                    <Dropdown value={simcard.network} onChange={(e) => onInputChange(e, 'network')}
                        options={networks} optionLabel="network" filter
                        placeholder={simcard.network ?? "Select network"} invalid={!simcard.network} />
                </div>
            </div>

            <div className="formgrid grid">
                <div className="field col">
                    <label className="mb-3 font-bold">PIN</label>
                    <InputText value={simcard.pin} maxLength={10} onChange={(e) => onInputChange(e, 'pin')} />
                    {simcard.pin && !validators.pin(simcard.pin) && <small className="p-error">Invalid PIN</small>}
                </div>
                <div className="field col">
                    <label className="mb-3 font-bold">PUK</label>
                    <InputText value={simcard.puk} maxLength={10} onChange={(e) => onInputChange(e, 'puk')} />
                    {simcard.puk && !validators.puk(simcard.puk) && <small className="p-error">Invalid PUK</small>}
                </div>
            </div>

            <div className="field">
                <label className="mb-3 font-bold">Notes</label>
                <ReactQuill theme="snow" value={simcard.notes || ''}
                    onChange={(value) => onInputChange({ value }, 'notes')} />
            </div>

            <div className="field-checkbox">
                <Checkbox inputId="pinDisabled" checked={!!simcard.pinDisabled}
                    onChange={(e) => onInputChange({ value: e.checked }, 'pinDisabled')} />
                <label htmlFor="pinDisabled" className="font-bold">PIN Disabled</label>
            </div>

            <div className="field">
                <label className="mb-3 font-bold">Image</label>
                <div className="flex items-center gap-4">
                    <FileUpload ref={fileUploadRef} mode="basic" accept="image/*" maxFileSize={5000000}
                        customUpload auto chooseLabel="Select Image"
                        onSelect={handleFileSelect}
                        onValidationFail={(file) => setUploadError(`"${file.name}" is too large - maximum size is 5 MB.`)} />
                    {uploadError && <small className="p-error block mt-1">{uploadError}</small>}
                    {simcard.imageData && (
                        <div className="relative w-32 border rounded shadow-sm flex items-center justify-center overflow-hidden"
                             style={{ height: "200px", flexDirection: "column", textAlign: "center" }}>
                            <Image src={`data:${simcard.imageMime};base64,${simcard.imageData}`}
                                alt="simcard image" width="128" preview className="border rounded shadow-sm" />
                            <button type="button"
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 transition"
                                onClick={() => {
                                    setSimcard(prev => ({ ...prev, imageData: null, imageMime: null }));
                                    fileUploadRef.current?.clear();
                                }}>
                                <i className="pi pi-times text-sm"></i>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </Dialog>
    );
};

const mapStateToProps = (state) => ({
    systemsNodesShort: state.dashboard.systemsNodesShort,
    systemsNodesRoutersShort: state.dashboard.systemsNodesRoutersShort,
    mobileView: state.dashboard.mobileView,
    dbSimcards: state.dashboard.simcards,
    currentSimcard: state.dashboard.currentSimcard,
    updateSimcardSuccess: state.dashboard.updateSimcardSuccess,
});

const actions = {
    getSystemsNodesShort,
    getSystemsNodesRoutersShort,
    updateSimcard, addSimcard, getSimcard
};

export default connect(mapStateToProps, actions)(SimcardEditDialog);